/**
 * Componente para gerenciar Time Conditions
 * VigosCloud - Message Context System
 * 
 * Gerencia condições de tempo para contextos de mensagem
 */

import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCalendarAlt, 
  faPlus, 
  faTrash,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';

import './MessageComponents.css';

function TimeConditionManager({ 
  rules = [],
  onChange,
  availableQueues = [],
  availableContexts = [],
  disabled = false,
  hasWhatsAppIntegration = false
}) {

  const addRule = () => {
    const newRules = [...rules, {
      time_condition: '*|*|*|*',
      destination_type: 'endpoint',
      destination: '',
      priority_order: rules.length + 1
    }];
    if (onChange) {
      onChange(newRules);
    }
  };

  const removeRule = (index) => {
    const newRules = rules.filter((_, i) => i !== index);
    // Reordenar priority_order
    newRules.forEach((rule, i) => {
      rule.priority_order = i + 1;
    });
    if (onChange) {
      onChange(newRules);
    }
  };

  const updateRule = (index, field, value) => {
    const newRules = [...rules];
    newRules[index] = {
      ...newRules[index],
      [field]: value
    };

    if (field === 'destination_type' && ['hangup', 'cycle', 'whatsapp'].includes(value)) {
      newRules[index].destination = '';
    }

    if (onChange) {
      onChange(newRules);
    }
  };

  const getAvailableDestinationTypes = () => {
    return [
      { value: 'endpoint', label: 'Ramal' },
      { value: 'queue', label: 'Fila' },
      { value: 'external', label: 'Externo' },
      { value: 'disa', label: 'DISA' },
      {
        value: 'whatsapp',
        label: hasWhatsAppIntegration ? 'WhatsApp' : 'WhatsApp (sem integração)',
        disabled: !hasWhatsAppIntegration
      },
      { value: 'cycle', label: 'Ciclo (Reiniciar)' },
      { value: 'hangup', label: 'Desligar' }
    ];
  };

  const renderDestinationInput = (rule, index) => {
    switch (rule.destination_type) {
      case 'endpoint':
        return (
          <input
            className="input is-small"
            type="text"
            placeholder="Ex: 1000"
            value={rule.destination || ''}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '').slice(0, 4);
              updateRule(index, 'destination', value);
            }}
            maxLength={4}
            disabled={disabled}
          />
        );
      
      case 'queue':
        return (
          <div className="select is-small is-fullwidth">
            <select
              value={rule.destination || ''}
              onChange={(e) => updateRule(index, 'destination', e.target.value)}
              disabled={disabled}
            >
              <option value="">Selecione uma fila</option>
              {availableQueues.map((queue) => (
                <option key={queue.namequeue || queue.name} value={queue.namequeue || queue.name}>
                  {queue.namequeue || queue.name}
                </option>
              ))}
            </select>
          </div>
        );
      
      case 'disa':
        return (
          <div className="select is-small is-fullwidth">
            <select
              value={rule.destination || ''}
              onChange={(e) => updateRule(index, 'destination', e.target.value)}
              disabled={disabled}
            >
              <option value="">Selecione um contexto</option>
              {availableContexts.map((context) => (
                <option key={context.name || context.context_name} value={context.name || context.context_name}>
                  {context.label || context.name || context.context_name}
                </option>
              ))}
            </select>
          </div>
        );
      
      case 'external':
        return (
          <input
            className="input is-small"
            type="text"
            placeholder="Ex: 1134567890"
            value={rule.destination || ''}
            onChange={(e) => updateRule(index, 'destination', e.target.value)}
            disabled={disabled}
          />
        );
      
      case 'whatsapp':
      case 'cycle':
      case 'hangup':
        return (
          <input
            className="input is-small"
            type="text"
            value={rule.destination_type.toUpperCase()}
            disabled
            style={{ backgroundColor: '#f5f5f5' }}
          />
        );
      
      default:
        return (
          <input
            className="input is-small"
            type="text"
            placeholder="Destino"
            value={rule.destination || ''}
            onChange={(e) => updateRule(index, 'destination', e.target.value)}
            disabled={disabled}
          />
        );
    }
  };

  return (
    <div className="form-section-card">
      <div className="level">
        <div className="level-left">
          <div className="level-item">
            <h3 className="title is-5">
              <FontAwesomeIcon icon={faCalendarAlt} className="mr-2" />
              Desvios por Data/Hora
            </h3>
          </div>
        </div>
        <div className="level-right">
          <div className="level-item">
            <button
              type="button"
              className="button is-info is-small"
              onClick={addRule}
              disabled={disabled}
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              Adicionar Regra
            </button>
          </div>
        </div>
      </div>

      {rules.length === 0 ? (
        <div className="notification is-light">
          <p className="has-text-centered">
            <strong>Nenhuma condição de tempo configurada</strong>
          </p>
          <p className="has-text-centered">
            Adicione regras para desviar chamadas baseadas em horário.
          </p>
        </div>
      ) : (
        <>
          <div className="notification is-info is-light mb-4">
            <h4 className="subtitle is-6">
              <FontAwesomeIcon icon={faInfoCircle} className="mr-2" />
              Exemplos de Configuração
            </h4>
            <div className="table-container">
              <table className="table is-fullwidth is-striped is-narrow is-size-7">
                <thead>
                  <tr>
                    <th>Descrição</th>
                    <th>Horário</th>
                    <th>Dias Semana</th>
                    <th>Dias Mês</th>
                    <th>Meses</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Comercial (Seg-Sex 8h-18h)</td>
                    <td><code>08:00-17:59</code></td>
                    <td><code>mon-fri</code></td>
                    <td><code>*</code></td>
                    <td><code>*</code></td>
                  </tr>
                  <tr>
                    <td>Fim de semana</td>
                    <td><code>*</code></td>
                    <td><code>sat-sun</code></td>
                    <td><code>*</code></td>
                    <td><code>*</code></td>
                  </tr>
                  <tr>
                    <td>Feriados (1º e 25)</td>
                    <td><code>*</code></td>
                    <td><code>*</code></td>
                    <td><code>1,25</code></td>
                    <td><code>*</code></td>
                  </tr>
                  <tr>
                    <td>Janeiro a Junho</td>
                    <td><code>*</code></td>
                    <td><code>*</code></td>
                    <td><code>*</code></td>
                    <td><code>jan-jun</code></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {rules.map((rule, index) => (
            <div key={index} className="box">
              <div className="level">
                <div className="level-left">
                  <div className="level-item">
                    <h4 className="subtitle is-6">Regra #{index + 1}</h4>
                  </div>
                </div>
                <div className="level-right">
                  <div className="level-item">
                    <button
                      type="button"
                      className="button is-small is-danger"
                      onClick={() => removeRule(index)}
                      disabled={disabled}
                      title="Remover regra"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </div>
              </div>

              <div className="columns">
                <div className="column is-3">
                  <div className="field">
                    <label className="label is-small">Horário</label>
                    <div className="control">
                      <input
                        className="input is-small"
                        type="text"
                        placeholder="08:00-18:00 ou *"
                        value={(rule.time_condition || '*|*|*|*').split('|')[0]}
                        onChange={(e) => {
                          const parts = (rule.time_condition || '*|*|*|*').split('|');
                          parts[0] = e.target.value || '*';
                          updateRule(index, 'time_condition', parts.join('|'));
                        }}
                        disabled={disabled}
                      />
                    </div>
                    <p className="help is-size-7">HH:MM-HH:MM ou *</p>
                  </div>
                </div>

                <div className="column is-3">
                  <div className="field">
                    <label className="label is-small">Dias da Semana</label>
                    <div className="control">
                      <input
                        className="input is-small"
                        type="text"
                        placeholder="mon-fri ou *"
                        value={(rule.time_condition || '*|*|*|*').split('|')[1]}
                        onChange={(e) => {
                          const parts = (rule.time_condition || '*|*|*|*').split('|');
                          parts[1] = e.target.value || '*';
                          updateRule(index, 'time_condition', parts.join('|'));
                        }}
                        disabled={disabled}
                      />
                    </div>
                    <p className="help is-size-7">mon-fri, sat-sun ou *</p>
                  </div>
                </div>

                <div className="column is-3">
                  <div className="field">
                    <label className="label is-small">Dias do Mês</label>
                    <div className="control">
                      <input
                        className="input is-small"
                        type="text"
                        placeholder="1-31 ou *"
                        value={(rule.time_condition || '*|*|*|*').split('|')[2]}
                        onChange={(e) => {
                          const parts = (rule.time_condition || '*|*|*|*').split('|');
                          parts[2] = e.target.value || '*';
                          updateRule(index, 'time_condition', parts.join('|'));
                        }}
                        disabled={disabled}
                      />
                    </div>
                    <p className="help is-size-7">1-31, 1,15 ou *</p>
                  </div>
                </div>

                <div className="column is-3">
                  <div className="field">
                    <label className="label is-small">Meses</label>
                    <div className="control">
                      <input
                        className="input is-small"
                        type="text"
                        placeholder="jan-dec ou *"
                        value={(rule.time_condition || '*|*|*|*').split('|')[3] || '*'}
                        onChange={(e) => {
                          const parts = (rule.time_condition || '*|*|*|*').split('|');
                          parts[3] = e.target.value || '*';
                          updateRule(index, 'time_condition', parts.join('|'));
                        }}
                        disabled={disabled}
                      />
                    </div>
                    <p className="help is-size-7">jan-dec, jan,jul ou *</p>
                  </div>
                </div>
              </div>

              <div className="columns">
                <div className="column is-6">
                  <div className="field">
                    <label className="label is-small">Tipo de Destino *</label>
                    <div className="control">
                      <div className="select is-small is-fullwidth">
                        <select
                          value={rule.destination_type || 'endpoint'}
                          onChange={(e) => updateRule(index, 'destination_type', e.target.value)}
                          disabled={disabled}
                        >
                          {getAvailableDestinationTypes().map(type => (
                            <option 
                              key={type.value} 
                              value={type.value}
                              disabled={type.disabled}
                            >
                              {type.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="column is-6">
                  <div className="field">
                    <label className="label is-small">Destino da Condição *</label>
                    <div className="control">
                      {renderDestinationInput(rule, index)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

export default TimeConditionManager;

